import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'stilos_cookie_consent';
const CONSENT_VERSION = '1.0';

const DEFAULT_CONSENT = {
  necessary: true,    // Always true, cannot be toggled
  analytics: false,
  marketing: false,
  preferences: false,
};

const CookieConsentContext = createContext({
  consent: DEFAULT_CONSENT,
  hasConsented: false,
  updateConsent: () => {},
  acceptAll: () => {},
  rejectAll: () => {},
  openPreferences: () => {},
  showBanner: false,
});

export const useCookieConsent = () => useContext(CookieConsentContext);

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(DEFAULT_CONSENT);
  const [hasConsented, setHasConsented] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.version === CONSENT_VERSION && parsed.consent) {
          setConsent({ ...DEFAULT_CONSENT, ...parsed.consent, necessary: true });
          setHasConsented(true);
          return;
        }
      }
    } catch {}
    // No valid consent found — show banner after a short delay
    const timer = setTimeout(() => setShowBanner(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const persist = useCallback((newConsent) => {
    const data = {
      version: CONSENT_VERSION,
      consent: newConsent,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setConsent(newConsent);
    setHasConsented(true);
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  const acceptAll = useCallback(() => {
    persist({ necessary: true, analytics: true, marketing: true, preferences: true });
  }, [persist]);

  const rejectAll = useCallback(() => {
    persist({ ...DEFAULT_CONSENT });
  }, [persist]);

  const updateConsent = useCallback((newConsent) => {
    persist({ ...newConsent, necessary: true });
  }, [persist]);

  const openPreferences = useCallback(() => {
    setShowPreferences(true);
    setShowBanner(true);
  }, []);

  return (
    <CookieConsentContext.Provider value={{
      consent, hasConsented, updateConsent, acceptAll, rejectAll,
      openPreferences, showBanner, showPreferences, setShowPreferences, setShowBanner,
    }}>
      {children}
    </CookieConsentContext.Provider>
  );
};
